import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import GuideTourContext from './GuideTour.context';
import {
  getArrayLength,
  isArrowLeftPressed,
  isArrowRightPressed,
  isEscapePressed,
  scrollToElement,
} from 'utils';
import useMatchMedia from 'hooks/useMatchMedia';
import { useNavigate } from 'react-router-dom';
import { KeyEvent } from 'types';
import GuideCard from 'components/GuideCard';
import useKeyDown from 'hooks/useKeyDown';
import useEscapeLayer from 'hooks/useEscapeLayer';
import { TRANSITION_TIME } from 'data/global';
import { Overlay } from 'ncoded-component-library';

type GuideStep = {
  selector: string;
  mobileSelector?: string;
  title: string;
  description: string;
  path?: string;
};

type GuideTourProps = {
  guideSteps: GuideStep[];
  onTourStart?: () => void;
  onStepView?: (stepIndex: number) => void;
  onTourComplete?: () => void;
  onTourClose?: (stepIndex: number) => void;
};

const GuideTour: React.FC<GuideTourProps> = (props) => {
  const {
    children,
    guideSteps,
    onTourStart,
    onStepView,
    onTourComplete,
    onTourClose,
  } = props;

  const [showGuide, setShowGuide] = useState(false);
  const [isStepVisible, setIsStepVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  const navigate = useNavigate();

  const isPhablet = useMatchMedia('isPhablet');

  const totalNumbeOfSteps = getArrayLength(guideSteps);
  const canUserSeeGuide = !!totalNumbeOfSteps;

  const selectedGuideStep = useMemo(
    () => guideSteps[currentStep],
    [currentStep, guideSteps],
  );

  const { selector, mobileSelector, title, description } =
    selectedGuideStep || {};

  const elementSelector = useMemo(
    () => (isPhablet && mobileSelector ? `#${mobileSelector}` : `#${selector}`),
    [isPhablet, mobileSelector, selector],
  );

  // the step swaps the card's title, description and anchor all at once, so it
  // has to commit while the card is invisible or the content changes mid-fade
  const fadeToStep = useCallback(
    (newStep: number) => {
      if (fadeTimeoutRef.current || newStep === currentStep) return;

      setIsStepVisible(false);

      fadeTimeoutRef.current = setTimeout(() => {
        fadeTimeoutRef.current = null;

        const { path } = guideSteps[newStep] || {};

        if (path) navigate(path, { replace: true });

        setCurrentStep(newStep);
        setIsStepVisible(true);
        onStepView?.(newStep);
      }, TRANSITION_TIME);
    },
    [currentStep, guideSteps, navigate, onStepView],
  );

  const handleNext = useCallback(
    () => fadeToStep((currentStep % totalNumbeOfSteps) + 1),
    [fadeToStep, currentStep, totalNumbeOfSteps],
  );

  const handleBack = useCallback(
    () => fadeToStep(Math.max(currentStep - 1, 0)),
    [fadeToStep, currentStep],
  );

  const onShowGuide = useCallback(() => {
    const newStepPath = guideSteps[0].path;

    if (newStepPath) navigate(newStepPath, { replace: true });

    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }

    setCurrentStep(0);
    setShowGuide(true);
    setIsStepVisible(true);
    onTourStart?.();
    onStepView?.(0);
  }, [guideSteps, navigate, onTourStart, onStepView]);

  // keep the card mounted through the fade out, then tear the tour down
  const onCloseOnboarding = useCallback(() => {
    if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);

    setIsStepVisible(false);

    fadeTimeoutRef.current = setTimeout(() => {
      fadeTimeoutRef.current = null;

      setCurrentStep(0);
      setShowGuide(false);
    }, TRANSITION_TIME);
  }, []);

  const handleDone = useCallback(() => {
    onTourComplete?.();
    onCloseOnboarding();
  }, [onTourComplete, onCloseOnboarding]);

  const handleClose = useCallback(() => {
    onTourClose?.(currentStep);
    onCloseOnboarding();
  }, [onTourClose, currentStep, onCloseOnboarding]);

  const isTopEscapeLayer = useEscapeLayer({
    enabled: canUserSeeGuide && showGuide,
  });

  const onKeyDown = useCallback(
    (event: KeyEvent) => {
      if (!canUserSeeGuide || !showGuide) return;

      if (isEscapePressed(event)) {
        // anything opened over the tour takes the press for itself
        if (isTopEscapeLayer()) handleClose();
        return;
      }

      // a pending fade already owns the next step, let it land first
      if (fadeTimeoutRef.current) return;

      if (isArrowRightPressed(event)) {
        if (currentStep === totalNumbeOfSteps - 1) handleDone();
        else handleNext();
        return;
      }

      if (isArrowLeftPressed(event)) {
        handleBack();
      }
    },
    [
      canUserSeeGuide,
      showGuide,
      currentStep,
      totalNumbeOfSteps,
      handleBack,
      handleNext,
      handleDone,
      handleClose,
      isTopEscapeLayer,
    ],
  );

  useEffect(() => {
    if (!showGuide || !selector) return;

    const id = requestAnimationFrame(() => scrollToElement(elementSelector));

    return () => cancelAnimationFrame(id);
  }, [currentStep, showGuide, selector, elementSelector]);

  useEffect(
    () => () => {
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    },
    [],
  );

  useKeyDown({ onKeyDown });

  return (
    <GuideTourContext.Provider value={{ onShowGuide }}>
      {children}
      {showGuide && (
        <>
          <Overlay
            background="black"
            className="guide-card-overlay"
            open
            noContent
            lockScroll
          />
          <GuideCard
            preferredPosition="bottom"
            title={title}
            description={description}
            stepNumber={currentStep + 1}
            totalSteps={totalNumbeOfSteps}
            showBannerNotification={isStepVisible}
            selector={elementSelector}
            onBack={handleBack}
            onNext={handleNext}
            onClose={handleClose}
            onDone={handleDone}
          />
        </>
      )}
    </GuideTourContext.Provider>
  );
};

export default GuideTour;
